import {Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Switch, Tooltip} from '@mui/material';
import {SettingsContext} from 'context/SettingsProvider';
import {type SupportedLanguages, Theme, URLPathname} from 'interfaces';
import useParseURL from 'hooks/useParseURL';
import {OptimizationRunsIcon} from 'styles/icons';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import InfoIcon from '@mui/icons-material/Info';
import InvertColorsIcon from '@mui/icons-material/InvertColors';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';

import {useEffect, useState, useContext, useRef} from 'react';
import {buildVersion} from 'utils/buildVersion';
import {EMPTY_ID, useGetAPIVersion} from 'hooks/swr';
import {useTranslation} from 'react-i18next';
import {useOnboardingContext} from 'context/OnboaringContext';
import {ICON_SIZE} from 'appConstants';
import {useModalWindow} from 'hooks/useModalWindow';
import {GlobalSettingsModal} from 'components/InputMenu/GlobalSettingsModal';
import {AppTopBarLanguageMenu} from './AppTopBarLanguageMenu';

export const AppTopBarMoreMenu = () => {
  const settingsContext = useContext(SettingsContext);
  const setThemeName = settingsContext?.setTheme;
  const themeName = settingsContext?.theme;
  const darkModeCheked = themeName === Theme.dark;
  const {navigate} = useParseURL();

  const {t} = useTranslation('common');
  const onboardingContext = useOnboardingContext();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const {openModalWindow} = useModalWindow();

  const {data: apiVersion} = useGetAPIVersion({resourceId: EMPTY_ID});

  useEffect(() => {
    if (onboardingContext.enabled) {
      setOpen(onboardingContext.isMoreMenuOpen ?? false);
    }
  }, [onboardingContext]);

  const handleToggle = () => {
    setOpen(!open);
    onboardingContext.enabled = false;
  };

  const handleClose = () => {
    setOpen(false);
    onboardingContext.enabled = false;
  };

  const toggleTheme = () => {
    if (themeName === Theme.light) {
      setThemeName && setThemeName(Theme.dark);
    } else {
      setThemeName && setThemeName(Theme.light);
    }
  };

  const onLanguageSelect = (option: SupportedLanguages) => {
    settingsContext?.setLanguage(option);
  };

  const goto = (pathname: string) => {
    return () => {
      navigate({pathname});
      handleClose();
    };
  };

  return (
    <>
      <Tooltip title={t('companyProperties') ?? ''}>
        <IconButton
          color="inherit"
          onClick={handleToggle}
          data-testid="settings-menu"
          id="onboarding-main-more-menu-button"
          ref={anchorRef}
        >
          <MoreVertIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <Menu
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        sx={{zIndex: 3000}}
        MenuListProps={{id: 'onboarding-main-more-menu'}}
      >
        <MenuItem onClick={goto(URLPathname.optimizationRuns)}>
          <ListItemIcon>
            <OptimizationRunsIcon fontSize={ICON_SIZE} />
          </ListItemIcon>
          <ListItemText>{t('optimizationRuns')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={goto(URLPathname.harvestReports)}>
          <ListItemIcon>
            <DescriptionIcon fontSize={ICON_SIZE} />
          </ListItemIcon>
          <ListItemText>{t('harvestReportsTitle')}</ListItemText>
        </MenuItem>

        <Divider variant="middle" />

        <MenuItem onClick={goto(URLPathname.connectionsSettings)} data-testid={URLPathname.connectionsSettings}>
          <ListItemIcon>
            <SettingsEthernetIcon fontSize={ICON_SIZE} />
          </ListItemIcon>
          <ListItemText>{t('connections')}</ListItemText>
        </MenuItem>
        <MenuItem data-testid="appbar-change-language">
          <AppTopBarLanguageMenu onLanguageSelect={onLanguageSelect} />
        </MenuItem>
        <MenuItem disableRipple onClick={toggleTheme}>
          <ListItemIcon>
            <InvertColorsIcon fontSize={ICON_SIZE} />
          </ListItemIcon>
          <ListItemText>{t('darkMode')}</ListItemText>
          <Switch checked={darkModeCheked} onChange={toggleTheme} size="small" onClick={toggleTheme} />
        </MenuItem>

        <MenuItem
          disableRipple
          onClick={() => {
            openModalWindow(GlobalSettingsModal);
            handleClose();
          }}
          data-testid="appbar-global-settings"
        >
          <ListItemIcon>
            <SettingsIcon fontSize={ICON_SIZE} />
          </ListItemIcon>
          <ListItemText>{t('globalSettings')}</ListItemText>
        </MenuItem>

        <Divider variant="middle" />

        <MenuItem disabled>
          <ListItemIcon>
            <InfoIcon fontSize={ICON_SIZE} />
          </ListItemIcon>
          <ListItemText>{`${t('version')}    ${buildVersion} / API ${apiVersion?.version}`}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
